/**
 * Student course sidebar: subjects → chapters → lesson items (Flutter-style hierarchy).
 */

import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { groupChaptersBySubject } from "../../../utils/courseSubjectGrouping";

const CourseSubjectChapterSidebar = ({
  chapters = [],
  subjects = [],
  progressPercent = 0,
  selectedChapterId,
  selectedContentId,
  isCompleted,
  getContentIcon,
  getContentTypeLabel,
  getEffectiveContentType,
  onContentSelect,
  isMobile = false,
  onClose,
}) => {
  const subjectGroups = useMemo(
    () => groupChaptersBySubject(chapters, subjects),
    [chapters, subjects],
  );

  const [expandedSubjectKey, setExpandedSubjectKey] = useState(null);
  const [expandedChapterId, setExpandedChapterId] = useState(null);

  useEffect(() => {
    if (!selectedChapterId || subjectGroups.length === 0) return;

    const subjectForChapter = subjectGroups.find((subject) =>
      subject.chapters.some((chapter) => chapter.id === selectedChapterId),
    );
    if (subjectForChapter) {
      setExpandedSubjectKey(String(subjectForChapter.id));
      setExpandedChapterId(selectedChapterId);
    }
  }, [selectedChapterId, subjectGroups]);

  const handleSubjectChange = (subjectKey) => (_, isExpanded) => {
    setExpandedSubjectKey(isExpanded ? subjectKey : null);
    if (!isExpanded) {
      setExpandedChapterId(null);
    }
  };

  const handleChapterChange = (chapterId) => (_, isExpanded) => {
    setExpandedChapterId(isExpanded ? chapterId : null);
  };

  return (
    <Box
      sx={{
        width: isMobile ? 300 : "100%",
        maxWidth: "100%",
        height: "100%",
        bgcolor: "#f4f6f9",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          bgcolor: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6" fontWeight={700}>
            Course Content
          </Typography>
          {isMobile && onClose && (
            <IconButton size="small" onClick={onClose} aria-label="Close menu">
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          {progressPercent}% complete
        </Typography>
      </Box>

      <Box sx={{ p: 1.5 }}>
        {subjectGroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
            No published lessons yet.
          </Typography>
        ) : (
          subjectGroups.map((subject) => {
            const subjectKey = String(subject.id);
            const publishedChapterCount = subject.chapters.length;

            return (
              <Accordion
                key={subjectKey}
                expanded={expandedSubjectKey === subjectKey}
                onChange={handleSubjectChange(subjectKey)}
                disableGutters
                elevation={0}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor:
                    expandedSubjectKey === subjectKey
                      ? "primary.light"
                      : "divider",
                  bgcolor: "#fff",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 52,
                    "& .MuiAccordionSummary-content": { my: 0.75 },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ width: "100%", pr: 0.5 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        {subject.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {publishedChapterCount} chapter
                        {publishedChapterCount === 1 ? "" : "s"}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0, pb: 1, px: 1 }}>
                  {subject.chapters.map((chapter) => {
                    const publishedItems =
                      chapter.items?.filter(
                        (item) => item.status === "published",
                      ) || [];
                    const chapterCompleted =
                      publishedItems.length > 0 &&
                      publishedItems.every((item) =>
                        isCompleted(chapter.id, item.id),
                      );

                    return (
                      <Accordion
                        key={chapter.id}
                        expanded={expandedChapterId === chapter.id}
                        onChange={handleChapterChange(chapter.id)}
                        disableGutters
                        elevation={0}
                        sx={{
                          mb: 0.75,
                          borderRadius: 1.5,
                          border: "1px solid #eceff3",
                          bgcolor: "#fafbfc",
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon fontSize="small" />}
                          onClick={(event) => {
                            if (publishedItems.length !== 1) return;
                            event.stopPropagation();
                            onContentSelect(chapter.id, publishedItems[0].id);
                          }}
                          sx={{
                            minHeight: 44,
                            px: 1,
                            "& .MuiAccordionSummary-content": { my: 0.5 },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{ width: "100%" }}
                          >
                            {chapterCompleted ? (
                              <CheckCircleIcon
                                color="success"
                                fontSize="small"
                                sx={{ mt: 0.25 }}
                              />
                            ) : (
                              <PlayCircleOutlineIcon
                                color="action"
                                fontSize="small"
                                sx={{ mt: 0.25 }}
                              />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  lineHeight: 1.35,
                                }}
                              >
                                {chapter.title}
                              </Typography>
                            </Box>
                            <Chip
                              label={publishedItems.length}
                              size="small"
                              sx={{ height: 22, flexShrink: 0 }}
                            />
                          </Stack>
                        </AccordionSummary>

                        <AccordionDetails sx={{ p: 0 }}>
                          <List dense disablePadding>
                            {publishedItems.map((item) => {
                              const completed = isCompleted(chapter.id, item.id);
                              const selected = selectedContentId === item.id;
                              const effectiveType = getEffectiveContentType(item);

                              return (
                                <ListItemButton
                                  key={item.id}
                                  selected={selected}
                                  onClick={() =>
                                    onContentSelect(chapter.id, item.id)
                                  }
                                  sx={{
                                    pl: 2.5,
                                    py: 0.75,
                                    borderLeft: selected
                                      ? "3px solid"
                                      : "3px solid transparent",
                                    borderColor: "primary.main",
                                    bgcolor: selected
                                      ? "primary.50"
                                      : "transparent",
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    {completed ? (
                                      <CheckCircleIcon
                                        color="success"
                                        fontSize="small"
                                      />
                                    ) : (
                                      getContentIcon(effectiveType)
                                    )}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={item.title}
                                    secondary={getContentTypeLabel(effectiveType)}
                                    primaryTypographyProps={{
                                      variant: "body2",
                                      fontWeight: selected ? 600 : 400,
                                      sx: {
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      },
                                    }}
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default CourseSubjectChapterSidebar;
